import { action } from "@ember/object"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import { tracked } from "@glimmer/tracking"
import type { CardAttachment } from "@shared/models/card-stack"
import Image from "@tiptap/extension-image"
import { handleError } from "cc-frontend/error-handling"
import type { AiValueEditor, RegisterAiValueEditor } from "cc-frontend/lib/ai/ai-value-editor"
import { CcTiptap } from "cc-frontend/lib/tiptap/cc-tiptap"
import type Dialog from "cc-frontend/services/dialog"
import type Filestack from "cc-frontend/services/filestack"
import { words } from "lodash-es"
import styles from "./scribe-editor.module.css"

interface Sig {
  Args: {
    value: string | null | undefined
    placeholder: string
    onValueUpdate?: (value: string) => void
    onFinishedEditing?: (
      value: string,
      originalValue: string | null,
      wordCountChange: number,
    ) => void
    registerEditor?: RegisterAiValueEditor
    addAttachments?: (files: Omit<CardAttachment, "id">[]) => void
    class?: string
  }
  Element: HTMLDivElement
}

export default class ScribeEditor extends Component<Sig> implements AiValueEditor<string> {
  readonly styles = styles

  @service declare dialog: Dialog
  @service declare filestack: Filestack

  private editorDiv!: HTMLElement
  private bubbleMenuDiv!: HTMLElement
  private tiptap: CcTiptap | null = null

  private originalValue: string | null = null
  private hasUpdated = false
  private focusInWordCount = 0
  private unregisterEditor?: () => void

  @tracked activeState: "focused" | "unfocused" | "captured" | "ai" = "unfocused"
  @tracked isTiptapEmpty = false
  @tracked isTiptapLoaded = false
  @tracked trCounter = 0
  @tracked isBubbleMenuOpen = false
  // The value to render as literal HTML when Tiptap is not yet loaded, else null.
  @tracked htmlValue: string | null = null

  constructor(owner: unknown, args: Sig["Args"]) {
    super(owner, args)

    this.setHtmlValue(args.value)

    this.unregisterEditor = this.args.registerEditor?.(this)
  }

  willDestroy(): void {
    this.unregisterEditor?.()
    this.tiptap?.destroy()
  }

  private setHtmlValue(value: string | null | undefined) {
    if (!value) this.htmlValue = ""
    else if (CcTiptap.isCcTiptapHtml(value)) {
      // Remove "disabled" attr from task checkboxes so they don't appear greyed out.
      this.htmlValue = value.replaceAll(
        /<input type="checkbox" disabled="(?:true)?"/g,
        '<input type="checkbox"',
      )
    } else {
      // value is an HTML string from Ckeditor.
      // Remove non-visible newlines and tabs that Ckeditor adds throughout the HTML.
      // Our html-value class renders these as literal newlines.
      this.htmlValue = value.replace(/[\n\t]/g, "")
    }
  }

  get isActive() {
    return this.activeState === "focused" || this.activeState === "captured"
  }

  get isEmpty() {
    if (this.isTiptapLoaded) {
      if (
        this.activeState === "ai" &&
        // @ts-expect-error AiChanges extension is missing extensionStorage type declaration
        this.tiptap!.editor.extensionStorage.aiChanges.getIsShowingAiChanges()
      ) {
        // When we are showing AI changes, the state may be empty but the editor
        // is trying to display a negative diff, which we need to reserve space for.
        return false
      } else return this.isTiptapEmpty
    } else {
      // Check if it's empty Ckeditor state (<p></p> or similar).
      // Empty Tiptap state is stored as "", which makes this easier.
      return /^(<\/?p>|\s)*$/.test(this.htmlValue || "")
    }
  }

  @action
  updateValue() {
    if (this.activeState === "ai") return

    if (this.tiptap === null) {
      this.setHtmlValue(this.args.value)
    } else {
      // Keep the user's in-progress edits. They'll overwrite the new value on focusout.
      if (this.isActive) return

      if (this.args.value !== this.tiptap.getHTML()) this.tiptap.setHTML(this.args.value ?? null)
    }
  }

  @action
  onFocusIn() {
    if (this.activeState !== "unfocused") return

    this.loadTiptap()

    this.originalValue = this.args.value ?? null
    this.hasUpdated = false
    this.focusInWordCount = words(this.tiptap!.editor.getText()).length
    this.activeState = "focused"
  }

  @action
  onFocusOut() {
    // Ignore if activeState == "captured" (the user is in a dialog that we opened).
    if (this.activeState !== "focused") return

    if (this.tiptap !== null && this.args.onFinishedEditing && this.hasUpdated) {
      const newValue = this.tiptap.getHTML()
      const focusOutWordCount = words(this.tiptap.editor.getText()).length
      // Uncomment to debug saved HTML.
      // console.log("onFinishedEditing", focusOutWordCount, newValue)
      this.args.onFinishedEditing(
        newValue,
        this.originalValue,
        focusOutWordCount - this.focusInWordCount,
      )
    }
    this.activeState = "unfocused"
  }

  // ============
  // Toolbar actions passed up to this component
  // ============

  @action
  addImage() {
    const editor = this.tiptap!.editor

    // Stay active even when unfocused due to the dialog.
    this.activeState = "captured"

    this.filestack.client
      .picker({
        fromSources: [
          "local_file_system",
          "imagesearch",
          "dropbox",
          "googledrive",
          "webcam",
          "onedrive",
          "onedriveforbusiness",
        ],
        maxFiles: 20,
        accept: ["image/*"],
        onUploadDone: (res) => {
          const chain = editor.chain()
          res.filesUploaded.forEach((file) => {
            chain.insertContent({ type: Image.name, attrs: { src: file.url } })
          })
          chain.run()
        },
        onClose: () => {
          editor.commands.focus()
          this.activeState = "focused"
        },
      })
      .open()
  }

  @action
  addLink() {
    const editor = this.tiptap!.editor

    // Stay active even when unfocused due to the dialog.
    this.activeState = "captured"

    if (editor.isActive("link")) {
      editor.commands.extendMarkRange("link")
    }

    const selectedText = editor.state.doc.textBetween(
      editor.state.selection.from,
      editor.state.selection.to,
    )
    const existingURL = editor.getAttributes("link").href ?? null
    this.dialog
      .blank("dialogs/link-as-attachment", {
        className: "dialog--create-annotation",
        title: selectedText,
        url: existingURL,
        // Allow unlinking by deleting the whole URL.
        allowEmptyUrl: existingURL !== null,
      })
      .then((response: { url: string; title: string; inputTitle: string }) => {
        const chain = editor.chain()
        if (
          (response.inputTitle && response.inputTitle !== selectedText) ||
          editor.state.selection.from === editor.state.selection.to
        ) {
          // Replace the selection with title, leaving it selected. Note that this will clobber any
          // other formatting or line breaks (same as GDocs).
          const selFrom = editor.state.selection.from
          chain
            .deleteSelection()
            .insertContent(response.title)
            .setTextSelection({ from: selFrom, to: selFrom + response.title.length })
        }
        if (response.url === "") {
          // The user erased an existing URL. Remove its link.
          chain.unsetLink()
        } else {
          chain.setLink({ href: response.url })
        }
        chain.run()
      })
      .catch((error) => {
        if (error instanceof Error) handleError(error)
      })
      .finally(() => {
        this.activeState = "focused"
      })
  }

  // ============
  // Tiptap setup
  // ============

  @action
  storeEditorDiv(editorDiv: HTMLElement) {
    this.editorDiv = editorDiv
  }

  @action
  storeBubbleMenuDiv(bubbleMenuDiv: HTMLElement) {
    this.bubbleMenuDiv = bubbleMenuDiv
    this.bubbleMenuDiv.remove()
  }

  @action
  loadTiptap() {
    if (this.tiptap !== null) return

    this.tiptap = new CcTiptap(this.editorDiv, this.args.value ?? null, {
      addLink: this.addLink.bind(this),
      filestack: this.filestack,
      bubbleMenuOptions: {
        element: this.bubbleMenuDiv,
        // Append to a div in application.hbs so we can appear on top of other lessons.
        appendTo: document.getElementById("cc-tiptap-bubble-menu-container")!,
        options: {
          // Placement relative to the text selection.
          placement: "top-start",
          offset: { mainAxis: 10, crossAxis: -50 },
          flip: { mainAxis: true, crossAxis: false, padding: 20 },
          onShow: () => {
            this.isBubbleMenuOpen = true
          },
          onHide: () => {
            this.isBubbleMenuOpen = false
          },
        },
      },
      addAttachments: this.args.addAttachments,
      onTransaction: this._onEditorTransaction.bind(this),
      onUpdate: this._onEditorUpdate.bind(this),
    })
    // Wait to replace the HTML value in the GUI until Tiptap is fully loaded,
    // to prevent flickering.
    this.tiptap.editor.on("create", this._onCreate)
  }

  private _onCreate = () => {
    this.tiptap!.editor.off("create", this._onCreate)
    this.isTiptapLoaded = true
    this.htmlValue = null
    this.isTiptapEmpty = this.tiptap!.isEmpty
  }

  private _onEditorTransaction() {
    // Increment this so that TiptapToolbar knows to re-read the editor state.
    this.trCounter++

    this.isTiptapEmpty = this.tiptap!.isEmpty
  }

  private _onEditorUpdate() {
    if (this.activeState === "ai") return

    this.hasUpdated = true
    if (this.args.onValueUpdate) {
      this.args.onValueUpdate(this.tiptap!.getHTML())
    }
  }

  // ============
  // AiValueEditor methods
  // ============

  startAiEdits(): void {
    this.loadTiptap()
    this.activeState = "ai"
    // To avoid a concerning big-deletion diff while the AI is still editing,
    // don't show AI changes until isDone.
    this.tiptap!.editor.chain().setShowAiChanges(false).startTrackingAiChanges().run()
  }

  setValueFromAi(aiValue: string, isDone: boolean): void {
    this.tiptap!.setHTML(aiValue)
    if (isDone) {
      this.tiptap!.editor.commands.setShowAiChanges(true)
    } else {
      // Re-set showAiChanges to false in case we set it to true earlier
      // but now the AI is revising (hence starting over with the value).
      this.tiptap!.editor.commands.setShowAiChanges(false)
    }
  }

  acceptAiEdits(): string {
    this.tiptap!.editor.chain().setShowAiChanges(false).stopTrackingAiChanges().run()
    const result = this.tiptap!.getHTML()
    this.activeState = "unfocused"

    return result
  }

  rejectAiEdits(): void {
    if (this.activeState !== "ai") return

    this.tiptap!.editor.chain().setShowAiChanges(false).stopTrackingAiChanges().run()
    this.activeState = "unfocused"
    this.updateValue()
  }
}

declare module "@glint/environment-ember-loose/registry" {
  export default interface Registry {
    ScribeEditor: typeof ScribeEditor
  }
}
